import {
  AccountData,
  Event,
  SavedFormResponse,
  SavedFormQuestion,
  Follower,
  FollowType,
  FollowerStatus,
  Campaign,
} from "@markit/common.types";
import {
  getEventData,
  getPhoneNumberData,
  getUserData,
  getUserEventsRef,
  getUserFollowerData,
  getUserFollowersRef,
  getUserSavedFormQuestionsRef,
  getUsersRef,
  getUserWishlistRef,
} from "./FirebaseUtils";
import { getDocs, limit, orderBy, query, where } from "../firebase";
import {
  filterUndefinedValues,
  isEventRelatedType,
  isProductSpecificType,
  SUBSCRIBED_CAMPAIGN_NAME,
} from "@markit/common.utils";
import { userCurrentNumberOfTickets } from "./eventUtils/userTicketUtils";
import { API } from "../API";
import { RecentEventAnalytics } from "../components/FollowerProfile/FollowerProfileHeader";
import { CampaignDetails } from "../redux/slices/campaignSlice";
import { LoginState } from "../redux/slices/accountSlice";

// Check if contact already has an account, and return the user data if it exists
export const fetchUserDataFromPhoneNumber = async (
  phoneNumber: string
): Promise<AccountData | undefined> => {
  const phoneData = await getPhoneNumberData(phoneNumber);

  return phoneData ? await getUserData(phoneData.userId) : undefined;
};

/**
 * Fetch for follower data using a phone number
 */
export const fetchFollowerDataFromPhoneNumber = async (
  uid: string,
  phoneNumber: string
): Promise<Follower | undefined> => {
  const phoneData = await getPhoneNumberData(phoneNumber);

  if (phoneData) {
    return getUserFollowerData(uid, phoneData.userId);
  } else {
    // No accountData exists yet for this phone number
    return undefined;
  }
};

/**
 * Fetch for user data using an email
 */
export const fetchUserDataFromEmail = async (
  email: string
): Promise<AccountData | undefined> => {
  const userRef = getUsersRef();
  const usersQuery = query(userRef, where("email", "==", email), limit(1));
  const snapshot = await getDocs(usersQuery);
  if (snapshot.empty) {
    return undefined;
  }
  return snapshot.docs.map((doc) => doc.data())[0];
};

// Checks if a follower status is "subscribed", meaning it's Subscribed or Invalid Number
export const checkIfFollowerIsSubscribed = (status: FollowerStatus) => {
  return (
    status === FollowerStatus.SUBSCRIBED ||
    status === FollowerStatus.INVALID_NUMBER
  );
};

// Checks if a specified user is subscribed to a designated user already
export const checkIfUserIsSubscribed = async (
  userId: string,
  userToFollowUid: string
): Promise<boolean> => {
  const followerData = await getUserFollowerData(userToFollowUid, userId);

  return followerData
    ? checkIfFollowerIsSubscribed(followerData.status)
    : false;
};

/**
 * Checks if the specified user can follow or is blocked by a condition
 */
export const checkIfUserCanFollow = async (
  userId: string,
  userToFollowUid: string
): Promise<boolean> => {
  // if userId is not defined, the user can follow
  if (userId === "") {
    return true;
  }

  const followerData = await getUserFollowerData(userToFollowUid, userId);

  // IF: followerData exists, can only follow if FollowerStatus is REMOVED.
  // Otherwise, it's subscribed, unsubscribed, blocked, or an invalid phone number verified by Twilio
  // ELSE: no followerData exists yet for this phone number for this creator, so can follow
  return followerData ? followerData.status === FollowerStatus.REMOVED : true;
};

/**
 * Checks if the specified phone number can follow or is blocked by a condition
 * Note: a bit redundant, but saves one firebase call compared to fetchUserDataFromPhoneNumber and checkIfUserCanFollow back-to-back
 */
export const checkIfPhoneNumberCanFollow = async (
  uid: string,
  phoneNumber: string
): Promise<boolean> => {
  const phoneData = await getPhoneNumberData(phoneNumber);

  if (phoneData) {
    return checkIfUserCanFollow(uid, phoneData.userId);
  } else {
    // No accountData exists yet for this phone number
    return true;
  }
};

/*
 * Checks if the specified user is unsubscribed
 * if userId is not defined, that means they aren't unsubscribed since we don't have record.
 */
export const checkIfUserIsUnsubscribed = async (
  userId: string,
  userToFollowUid: string
): Promise<boolean> => {
  // if userId is not defined, the user is not unsubscribed
  if (userId === "") {
    return false;
  }

  // check if they're unsubscribed to the free creator number if this is a free tier creator
  const creatorData = await getUserData(userToFollowUid);
  if (creatorData && creatorData.customer.phone === "") {
    const userData = await getUserData(userId);
    if (userData && userData.phoneNumber) {
      const phoneNumberData = await getPhoneNumberData(userData.phoneNumber);
      if (phoneNumberData && phoneNumberData.optOutFreeCreatorPhone) {
        return true;
      }
    }
  }

  const followerData = await getUserFollowerData(userToFollowUid, userId);

  return followerData
    ? followerData.status === FollowerStatus.UNSUBSCRIBED
    : false;
};

/*
 * Checks if the specified phone number is unsubscribed
 * if phone number is not defined, that means they aren't unsubscribed since we don't have record.
 */
export const checkIfPhoneNumberIsUnsubscribed = async (
  phoneNumber: string,
  userToFollowUid: string
): Promise<boolean> => {
  // if phone number is not defined, the user is not unsubscribed
  if (phoneNumber === "") {
    return false;
  }

  // check if they're unsubscribed to the free creator number if this is a free tier creator
  const creatorData = await getUserData(userToFollowUid);
  if (creatorData && creatorData.customer.phone === "") {
    const phoneNumberData = await getPhoneNumberData(phoneNumber);
    if (phoneNumberData && phoneNumberData.optOutFreeCreatorPhone) {
      return true;
    }
  }

  const followerData = await fetchFollowerDataFromPhoneNumber(
    userToFollowUid,
    phoneNumber
  );

  return followerData
    ? followerData.status === FollowerStatus.UNSUBSCRIBED
    : false;
};

/*
 * Checks if the specified user is unsubscribed from markit number
 * if userId is not defined, that means they aren't unsubscribed since we don't have record.
 */
export const checkIfUserIsUnsubscribedMarkit = async (
  phoneNumber: string
): Promise<boolean> => {
  // if userId is not defined, the user is not unsubscribed
  if (phoneNumber === "") {
    return false;
  }

  // Even if using firebase OTP, we need them opted in so they can get confirmation texts
  const phoneNumberData = await getPhoneNumberData(phoneNumber);
  if (phoneNumberData) {
    return phoneNumberData.optOut;
  } else {
    return false;
  }
};

// The user is a creator based account if they have an email or a phone number and are in creator mode
export const isCreatorBasedAccount = (userData: AccountData) =>
  userData.email || (userData.phoneNumber && userData.inCreatorMode);

// Just a helper function to consolidate the logic for checking if the logged in account is a phone number account
export const checkIfLoggedInPhoneNumber = (
  loggedIn: LoginState,
  phoneNumber: string
) => {
  return loggedIn === LoginState.LOGGED_IN && phoneNumber;
};

export const fetchUserFollowerData = async (
  creatorUid: string,
  userId: string
): Promise<{ follower: Follower | undefined; event: Event | undefined }> => {
  const followerData = await getUserFollowerData(creatorUid, userId);

  if (followerData) {
    if (followerData.eventId !== "") {
      const eventData = await getEventData(followerData.eventId);
      return { follower: followerData, event: eventData };
    } else {
      return { follower: followerData, event: undefined };
    }
  } else {
    console.log("No follower user found: " + userId);
    return { follower: undefined, event: undefined };
  }
};

export const fetchMostRecentUserAttendedEvents = async (
  creatorUid: string,
  userId: string,
  type: "event" | "product",
  numTicketsToDisplay?: number
) => {
  const userWishlistRef = getUserWishlistRef(userId);
  const userWishlistQuery = query(
    userWishlistRef,
    orderBy("createdAt", "desc")
  );
  const snapshot = await getDocs(userWishlistQuery);
  // Make sure only one wishmark per event is fetched
  const seenEventIds = new Set<string>();
  const uniqueWishDocs = snapshot.docs.filter((doc) => {
    const eventId = doc.data().eventId;
    if (!eventId) {
      return false;
    }
    if (seenEventIds.has(eventId)) {
      return false;
    }
    seenEventIds.add(eventId);
    return true;
  });

  const eventAnalytics: ({ event: Event; numTickets: number } | undefined)[] =
    await Promise.all(
      uniqueWishDocs.map(async (doc) => {
        const wishMark = doc.data();
        if (!wishMark.eventId) {
          return undefined;
        }
        const eventsRef = getUserEventsRef(creatorUid);
        const eventsQuery = query(
          eventsRef,
          where("id", "==", wishMark.eventId),
          limit(1)
        );
        const eventSnapshot = await getDocs(eventsQuery);
        if (eventSnapshot.empty) {
          return undefined;
        }
        const event = eventSnapshot.docs.map((doc) => doc.data())[0];

        if (
          (type === "event" && isEventRelatedType(event.eventType)) ||
          (type === "product" && isProductSpecificType(event.eventType))
        ) {
          const numTickets = await userCurrentNumberOfTickets(
            wishMark.eventId,
            userId
          );
          return { event: event, numTickets: numTickets };
        }

        return undefined;
      })
    );
  const definedEventAnalytics: RecentEventAnalytics[] =
    filterUndefinedValues(eventAnalytics);
  const sortedDefinedEventAnalytics = definedEventAnalytics.sort((x, y) => {
    return new Date(y.event.end).getTime() - new Date(x.event.end).getTime();
  });
  return numTicketsToDisplay
    ? sortedDefinedEventAnalytics.slice(0, numTicketsToDisplay)
    : sortedDefinedEventAnalytics;
};

// Fetches for the specified user's essential question
export const fetchUserEssentialQuestion = async (
  uid: string
): Promise<SavedFormQuestion | undefined> => {
  const savedFormQuestionsRef = getUserSavedFormQuestionsRef(uid);

  const essentialQuestionQuery = query(
    savedFormQuestionsRef,
    where("isEssential", "==", true)
  );

  const essentialQuestionSnap = await getDocs(essentialQuestionQuery);
  if (essentialQuestionSnap.empty) {
    return undefined;
  }
  return essentialQuestionSnap.docs.map((question) => question.data())[0];
};

// Fetches for the specified user's essential form response
export const fetchUserEssentialFormResponse = async (
  userId: string,
  followerId: string,
  essentialQuestionId: string
): Promise<SavedFormResponse> => {
  const { formResponse } = await API.user.fetchUserEssentialFormResponse({
    uid: userId,
    followerId: followerId,
    essentialFormQuestionId: essentialQuestionId,
  });
  return formResponse;
};

// Fetches for the specified user's email form response, if applicable
export const fetchUserEmailFormResponse = async (
  userId: string,
  followerId: string
): Promise<SavedFormResponse> => {
  const { formResponse } = await API.user.fetchUserEmailFormResponse({
    uid: userId,
    followerId: followerId,
  });
  return formResponse;
};

// Fetches for the specified user's essential form response
export const fetchMultipleUserFormResponses = async (
  userId: string,
  followerId: string,
  questionIds: string[]
): Promise<(SavedFormResponse | undefined)[]> => {
  const { formResponses } = await API.user.fetchMultipleUserFormResponses({
    uid: userId,
    followerId: followerId,
    questionIds: questionIds,
  });
  return formResponses;
};

// Checks the followersData and how many recipients have not been sent compliance
export const fetchNumFollowersNoComplianceSent = (
  followersData: Follower[],
  recipients: string[]
) => {
  const numFollowersNotCompliant = followersData.filter(
    (follower) =>
      !follower.complianceMessageSent && recipients.includes(follower.uid)
  ).length;
  return numFollowersNotCompliant;
};

// Returns the number of followers of a specific follow type
export const fetchNumFollowTypeFollowers = async (
  userId: string,
  followType: FollowType
): Promise<number> => {
  const followersRef = getUserFollowersRef(userId);
  const query_ = query(followersRef, where("followType", "==", followType));
  const snapshot = await getDocs(query_);
  return snapshot.size;
};

// Checks if the user has any saved questions or if the subscribe trigger campaign has been updated
// Used for post onboarding checklist
export const checkUpdatedWelcomeFlow = (
  savedQuestions: SavedFormQuestion[],
  campaigns: Campaign[],
  campaignDetails: CampaignDetails[]
) => {
  const hasProfileQuestions = savedQuestions.some(
    (question) => question.onProfileFollow
  );
  if (hasProfileQuestions) {
    return true;
  }
  const foundSubscribeCampaign = campaigns.find(
    (campaign) => campaign.title === SUBSCRIBED_CAMPAIGN_NAME
  );
  if (foundSubscribeCampaign) {
    const foundCampaignDetails = campaignDetails.find(
      (detail) => detail.campaignId === foundSubscribeCampaign.id
    );
    if (
      foundCampaignDetails &&
      foundCampaignDetails.massTextDetails.length > 0
    ) {
      const foundMassText = foundCampaignDetails.massTextDetails[0].massText;
      return !foundMassText.isDefault;
    }
  }
  return false;
};
